'use client';

import { useEffect, useRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { trackClick } from '@/lib/analytics';

type Props = ComponentPropsWithoutRef<'details'> & {
  trackEventId: string;
  openLabel: string;
  closeLabel: string;
  children: ReactNode;
};

/**
 * Wraps <details> and fires trackClick on toggle when JS runs. No-op without JS.
 */
export default function TrackedDetails({
  trackEventId,
  openLabel,
  closeLabel,
  children,
  ...rest
}: Props) {
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onToggle = () => {
      trackClick(
        trackEventId,
        undefined,
        undefined,
        el.open ? openLabel : closeLabel,
      );
    };

    el.addEventListener('toggle', onToggle);
    return () => el.removeEventListener('toggle', onToggle);
  }, [trackEventId, openLabel, closeLabel]);

  return (
    <details ref={ref} {...rest}>
      {children}
    </details>
  );
}
